/**
 * Firestore session sync for a single shared listening space.
 *
 * Event, sync/queue, and settings are shared.
 * sync/player is a guest display anchor and a host recovery checkpoint.
 * Only the host mounts YouTube, owns the playback clock, and publishes player state.
 * Guests never publish player state and never issue YouTube playback commands.
 */
import {
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db, getAuthUid } from '../firebase';
import useEventStore, { setSettingsPublisher } from '../stores/eventStore';
import usePlayerStore, { setPlayerPublisher } from '../stores/playerStore';
import useQueueStore, { setQueuePublisher } from '../stores/queueStore';
import { useRoleStore } from '../stores/roleStore';
import { isEventHost } from '../utils/identity';
import { isEventLive } from '../utils/eventStatus';
import {
  canStartPlayerHeartbeat,
  hasPlayerCheckpoint,
  PLAYER_HEARTBEAT_MS,
  shouldApplyPlayerVersion,
  shouldRunHeartbeat,
} from '../utils/playbackClock';
import { p2Log, p2LogSnap } from '../utils/p2Diagnostics';
import {
  clearPendingHostSeek,
  resetHostSeekForTests,
  setHostSeekCallbacks,
  shouldSuppressHeartbeat,
} from '../utils/hostSeek';
import {
  canAppendToQueue,
  canPublishSettings,
  canRemoveOrReorderQueue,
  commitQueueAppend,
  commitQueueRemove,
  commitQueueReorder,
  createEmptyPlayerDoc,
  createEmptyQueueDoc,
  needsSchemaV2,
  normalizeQueueItem,
  shouldApplyQueueRevision,
} from '../utils/queueLogic';
import { markPresenceOnline, watchPresence } from './presence';

export const HOST_PLAYER_RECOVERY_TIMEOUT_MS = 5000;
const PLAYER_REASONS = new Set([
  'play',
  'pause',
  'seek',
  'skip',
  'previous',
  'song',
  'heartbeat',
  'ended',
]);

let activeEventId = null;
let unsubscribers = [];
let lastAppliedQueueRevision = -1;
let lastWrittenQueueRevision = -1;
let lastAppliedPlayerVersion = -1;
let lastWrittenPlayerVersion = -1;
let applyingRemote = false;
let applyingRemotePlayer = false;
let heartbeatTimer = null;
let recoveryTimer = null;
let publishCalls = 0;

function eventRef(eventId) {
  return doc(db, 'events', eventId);
}

function queueRef(eventId) {
  return doc(db, 'events', eventId, 'sync', 'queue');
}

export async function fetchQueueSnapshot(eventId) {
  if (!eventId) return [];
  try {
    const snap = await getDoc(queueRef(eventId));
    if (!snap?.exists?.()) return [];
    const items = snap.data()?.items;
    return Array.isArray(items) ? items : [];
  } catch (error) {
    console.error('Failed to load queue snapshot:', error);
    return [];
  }
}

function playerRef(eventId) {
  return doc(db, 'events', eventId, 'sync', 'player');
}

function currentIdentity() {
  const { userId, hostId } = useRoleStore.getState();
  return { userId: userId || getAuthUid(), hostId };
}

function currentGuestRequestsEnabled() {
  const settings = useEventStore.getState().eventData?.settings;
  return settings?.guestRequestsEnabled === true;
}

export function getActiveEventId() {
  return activeEventId;
}

export function getLastAppliedQueueRevision() {
  return lastAppliedQueueRevision;
}

export function getPublishCallCount() {
  return publishCalls;
}

export function getLastAppliedPlayerVersion() {
  return lastAppliedPlayerVersion;
}

export function getLastWrittenPlayerVersion() {
  return lastWrittenPlayerVersion;
}

export function isPlayerHeartbeatActive() {
  return heartbeatTimer != null;
}

export function setPlayerSyncVersionsForTests(applied, written) {
  lastAppliedPlayerVersion = applied;
  lastWrittenPlayerVersion = written;
}

function readActualPlayerPosition(fallback) {
  const player = usePlayerStore.getState().playerInstance;
  try {
    const time = player?.getCurrentTime?.();
    if (typeof time === 'number' && Number.isFinite(time) && time >= 0) {
      return time;
    }
  } catch {
    // Fall through to Zustand.
  }
  return Math.max(0, Number(fallback) || 0);
}

function isHostRecoveryPending() {
  return usePlayerStore.getState().playerRecoveryStatus === 'pending';
}

function bootstrapHostPlayerVersions(version) {
  const safe = Number(version);
  const baseline = Number.isFinite(safe) && safe > 0 ? safe : 0;
  lastAppliedPlayerVersion = baseline;
  lastWrittenPlayerVersion = baseline;
}

function clearHostRecoveryWatchdog() {
  if (recoveryTimer != null) {
    clearTimeout(recoveryTimer);
    recoveryTimer = null;
  }
}

function finishHostRecoveryNoCheckpoint(version = 0) {
  const { userId, hostId } = currentIdentity();
  if (!isEventHost(userId, hostId)) return;
  if (!isHostRecoveryPending()) return;
  bootstrapHostPlayerVersions(version);
  usePlayerStore.getState().setPlayerRecoveryStatus('no-checkpoint');
  clearHostRecoveryWatchdog();
}

function finishHostRecoveryFromCheckpoint(data) {
  applyingRemotePlayer = true;
  usePlayerStore.getState().applyHostRecovery({
    song: data.song,
    isPlaying: Boolean(data.isPlaying),
    positionSeconds: data.positionSeconds,
    updatedAtMs: data.updatedAtMs,
  });
  applyingRemotePlayer = false;
  bootstrapHostPlayerVersions(Number(data?.version) || 0);
  clearHostRecoveryWatchdog();
  return true;
}

function startHostRecoveryWatchdog() {
  clearHostRecoveryWatchdog();
  recoveryTimer = setTimeout(() => {
    recoveryTimer = null;
    finishHostRecoveryNoCheckpoint(0);
  }, HOST_PLAYER_RECOVERY_TIMEOUT_MS);
}

function snapFields(data) {
  return {
    version: Number(data?.version) || 0,
    reason: data?.reason ?? null,
    position: data?.positionSeconds ?? null,
    isPlaying: Boolean(data?.isPlaying),
    songId: data?.song?.id ?? null,
  };
}

export function handleRemotePlayerSnapshot(data) {
  const { userId, hostId } = currentIdentity();
  const version = Number(data?.version) || 0;
  const fields = snapFields(data);

  if (isEventHost(userId, hostId)) {
    if (isHostRecoveryPending()) {
      if (hasPlayerCheckpoint(data)) {
        const applied = finishHostRecoveryFromCheckpoint(data);
        p2LogSnap({ ...fields, applied: true, why: 'host-bootstrap' });
        return applied;
      }
      finishHostRecoveryNoCheckpoint(version);
      p2LogSnap({ ...fields, applied: false, why: 'host-no-checkpoint' });
      return false;
    }
    if (version > lastAppliedPlayerVersion) {
      lastAppliedPlayerVersion = version;
    }
    p2LogSnap({ ...fields, applied: false, why: 'host' });
    return false;
  }

  if (!shouldApplyPlayerVersion(version, lastAppliedPlayerVersion, lastWrittenPlayerVersion)) {
    p2LogSnap({ ...fields, applied: false, why: 'stale-or-echo' });
    return false;
  }

  applyingRemotePlayer = true;
  usePlayerStore.getState().applyRemotePlayer({
    song: data.song ?? null,
    isPlaying: Boolean(data.isPlaying),
    positionSeconds: data.positionSeconds,
    updatedAtMs: data.updatedAtMs,
  });
  lastAppliedPlayerVersion = version;
  applyingRemotePlayer = false;
  p2LogSnap({ ...fields, applied: true });
  return true;
}

export function stopPlayerHeartbeat() {
  if (heartbeatTimer != null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

export function startPlayerHeartbeat() {
  const { userId, hostId } = currentIdentity();
  const { isPlaying, playerRecoveryStatus } = usePlayerStore.getState();
  if (!canStartPlayerHeartbeat({
    isHost: isEventHost(userId, hostId),
    isPlaying,
    recoveryStatus: playerRecoveryStatus,
  })) {
    stopPlayerHeartbeat();
    return false;
  }

  stopPlayerHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (shouldSuppressHeartbeat()) {
      p2Log('[p2-host-seek]', { phase: 'heartbeat-suppressed' });
      return;
    }
    const { userId: uid, hostId: hid } = currentIdentity();
    const playing = usePlayerStore.getState().isPlaying;
    if (!shouldRunHeartbeat({ isHost: isEventHost(uid, hid), isPlaying: playing })) {
      stopPlayerHeartbeat();
      return;
    }
    const positionSeconds = readActualPlayerPosition(usePlayerStore.getState().currentTime);
    publishPlayerState('heartbeat', { positionSeconds, isPlaying: true });
  }, PLAYER_HEARTBEAT_MS);
  return true;
}

function syncHeartbeatAfterPublish(isPlaying, reason) {
  if (isPlaying && reason !== 'pause' && reason !== 'ended') {
    if (reason === 'heartbeat' && heartbeatTimer != null) return;
    startPlayerHeartbeat();
    return;
  }
  stopPlayerHeartbeat();
}

export async function publishPlayerState(reason, extras = {}) {
  if (applyingRemotePlayer) return false;
  const { userId, hostId } = currentIdentity();
  if (!isEventHost(userId, hostId)) return false;
  if (isHostRecoveryPending()) return false;
  if (!currentEventIsLive()) return false;

  const eventId = activeEventId || useEventStore.getState().eventData?.id;
  if (!eventId) return false;

  const state = usePlayerStore.getState();
  const song = extras.song !== undefined ? extras.song : state.currentSong;
  const isPlaying = extras.isPlaying !== undefined ? extras.isPlaying : state.isPlaying;
  const positionSeconds = extras.positionSeconds != null
    ? Math.max(0, Number(extras.positionSeconds) || 0)
    : readActualPlayerPosition(state.currentTime);
  const nextVersion = Math.max(lastWrittenPlayerVersion, lastAppliedPlayerVersion, 0) + 1;
  const safeReason = PLAYER_REASONS.has(reason) ? reason : 'song';

  try {
    await setDoc(playerRef(eventId), {
      song: song || null,
      isPlaying: Boolean(isPlaying),
      positionSeconds,
      updatedAtMs: Date.now(),
      updatedAt: serverTimestamp(),
      version: nextVersion,
      reason: safeReason,
      updatedBy: userId,
    });
    lastWrittenPlayerVersion = nextVersion;
    if (nextVersion > lastAppliedPlayerVersion) {
      lastAppliedPlayerVersion = nextVersion;
    }
    publishCalls += 1;
    syncHeartbeatAfterPublish(Boolean(isPlaying), safeReason);
    return true;
  } catch (error) {
    console.error('Failed to publish player state:', error);
    return false;
  }
}

export function handleRemoteQueueSnapshot(data) {
  const revision = Number(data?.revision) || 0;
  if (
    !shouldApplyQueueRevision(revision, lastAppliedQueueRevision)
    || revision <= lastWrittenQueueRevision
  ) {
    return false;
  }

  applyingRemote = true;
  useQueueStore.getState().applyRemoteQueue(data.items || [], revision);
  lastAppliedQueueRevision = revision;
  applyingRemote = false;
  return true;
}

function currentEventIsLive() {
  return isEventLive(useEventStore.getState().eventData);
}

export function handleRemoteEventSnapshot(eventId, data) {
  applyingRemote = true;
  useEventStore.getState().applyRemoteEvent({
    id: eventId,
    title: data.title,
    hostId: data.hostId,
    createdAt: data.createdAt,
    participants: data.participants,
    settings: data.settings,
    schemaVersion: data.schemaVersion,
    status: data.status,
    endedAt: data.endedAt,
    endedBy: data.endedBy,
  });
  applyingRemote = false;
}

export async function initializeEventSchema(eventId, { legacyQueue = [] } = {}) {
  const { userId } = currentIdentity();
  const seeded = (legacyQueue || [])
    .map((item) => normalizeQueueItem(item, userId))
    .filter(Boolean);

  const queueSnap = await getDoc(queueRef(eventId));
  if (!queueSnap?.exists?.()) {
    await setDoc(queueRef(eventId), {
      ...createEmptyQueueDoc(userId, seeded),
      updatedAt: serverTimestamp(),
    });
  }

  const playerSnap = await getDoc(playerRef(eventId));
  if (!playerSnap?.exists?.()) {
    await setDoc(playerRef(eventId), createEmptyPlayerDoc());
  }

  await updateDoc(eventRef(eventId), { schemaVersion: 2 });
}

export async function ensureSchemaV2ForHost(eventId) {
  const { userId, hostId } = currentIdentity();
  if (!isEventHost(userId, hostId)) return false;

  const eventSnap = await getDoc(eventRef(eventId));
  if (!eventSnap?.exists?.()) return false;

  const eventData = eventSnap.data() || {};
  const queueSnap = await getDoc(queueRef(eventId));
  const playerSnap = await getDoc(playerRef(eventId));
  const queueExists = Boolean(queueSnap?.exists?.());
  const playerExists = Boolean(playerSnap?.exists?.());

  if (!needsSchemaV2(eventData, queueExists, playerExists)) return false;

  await initializeEventSchema(eventId, { legacyQueue: eventData.queue || [] });
  return true;
}

async function mutateQueue(eventId, mutator) {
  if (applyingRemote) return false;
  if (!eventId) return false;

  const revision = await runTransaction(db, async (transaction) => {
    const ref = queueRef(eventId);
    const snap = await transaction.get(ref);
    const current = snap.exists()
      ? snap.data()
      : { items: [], revision: 0 };
    const result = mutator(current);
    if (!result.ok) {
      throw new Error(result.reason || 'queue-mutation-failed');
    }
    transaction.set(ref, {
      ...result.doc,
      updatedAt: serverTimestamp(),
      updatedBy: currentIdentity().userId,
    });
    return result.doc.revision;
  });

  lastWrittenQueueRevision = revision;
  if (revision > lastAppliedQueueRevision) {
    lastAppliedQueueRevision = revision;
  }
  publishCalls += 1;
  return true;
}

export async function publishQueueAppend(item) {
  if (applyingRemote) return false;
  const eventId = activeEventId || useEventStore.getState().eventData?.id;
  const { userId, hostId } = currentIdentity();
  if (!canAppendToQueue({
    userId,
    hostId,
    guestRequestsEnabled: currentGuestRequestsEnabled(),
    eventLive: currentEventIsLive(),
  })) {
    return false;
  }

  const normalized = normalizeQueueItem(item, userId);
  try {
    return await mutateQueue(eventId, (doc) => commitQueueAppend(doc, normalized));
  } catch (error) {
    console.error('Failed to append queue item:', error);
    return false;
  }
}

export async function publishQueueRemove(index) {
  if (applyingRemote) return false;
  const eventId = activeEventId || useEventStore.getState().eventData?.id;
  if (!canRemoveOrReorderQueue({ ...currentIdentity(), eventLive: currentEventIsLive() })) return false;
  try {
    return await mutateQueue(eventId, (doc) => commitQueueRemove(doc, index));
  } catch (error) {
    console.error('Failed to remove queue item:', error);
    return false;
  }
}

export async function publishQueueReorder(fromIndex, toIndex) {
  if (applyingRemote) return false;
  const eventId = activeEventId || useEventStore.getState().eventData?.id;
  if (!canRemoveOrReorderQueue({ ...currentIdentity(), eventLive: currentEventIsLive() })) return false;
  try {
    return await mutateQueue(eventId, (doc) => commitQueueReorder(doc, fromIndex, toIndex));
  } catch (error) {
    console.error('Failed to reorder queue:', error);
    return false;
  }
}

export async function publishEventSettings(patch) {
  if (applyingRemote) return false;
  const eventId = activeEventId || useEventStore.getState().eventData?.id;
  const { userId, hostId } = currentIdentity();
  if (!eventId || !canPublishSettings({ userId, hostId, eventLive: currentEventIsLive() })) return false;

  const current = useEventStore.getState().eventData?.settings || {};
  try {
    await updateDoc(eventRef(eventId), {
      settings: { ...current, ...patch },
    });
    publishCalls += 1;
    return true;
  } catch (error) {
    console.error('Failed to publish settings:', error);
    return false;
  }
}

function bindPublishers() {
  setQueuePublisher({
    append: (item) => { publishQueueAppend(item); },
    remove: (index) => { publishQueueRemove(index); },
    reorder: (from, to) => { publishQueueReorder(from, to); },
  });
  setSettingsPublisher((patch) => { publishEventSettings(patch); });
  setPlayerPublisher((reason, extras) => { publishPlayerState(reason, extras); });
  setHostSeekCallbacks({
    onConfirmed: (snapshot) => {
      publishPlayerState('seek', {
        positionSeconds: snapshot.actualTime,
        isPlaying: snapshot.wantPlaying,
      });
    },
    getActualTime: () => {
      const player = usePlayerStore.getState().playerInstance;
      try {
        const time = player?.getCurrentTime?.();
        if (typeof time === 'number' && Number.isFinite(time) && time >= 0) {
          return time;
        }
      } catch {
        return null;
      }
      return null;
    },
  });
}

function unbindPublishers() {
  setQueuePublisher(null);
  setSettingsPublisher(null);
  setPlayerPublisher(null);
  setHostSeekCallbacks({});
}

export function unsubscribeFromEventSession() {
  clearPendingHostSeek();
  clearHostRecoveryWatchdog();
  stopPlayerHeartbeat();
  usePlayerStore.getState().setPlayerRecoveryStatus('idle');
  unsubscribers.forEach((unsubscribe) => {
    if (typeof unsubscribe === 'function') unsubscribe();
  });
  unsubscribers = [];
  activeEventId = null;
  lastAppliedQueueRevision = -1;
  lastWrittenQueueRevision = -1;
  lastAppliedPlayerVersion = -1;
  lastWrittenPlayerVersion = -1;
  applyingRemote = false;
  applyingRemotePlayer = false;
  unbindPublishers();
}

export function subscribeToEventSession(eventId) {
  if (!eventId) {
    unsubscribeFromEventSession();
    return unsubscribeFromEventSession;
  }

  if (activeEventId === eventId && unsubscribers.length > 0) {
    return unsubscribeFromEventSession;
  }

  unsubscribeFromEventSession();
  activeEventId = eventId;
  lastAppliedQueueRevision = -1;
  lastWrittenQueueRevision = -1;
  lastAppliedPlayerVersion = -1;
  lastWrittenPlayerVersion = -1;
  bindPublishers();

  const { userId, hostId } = currentIdentity();
  if (isEventHost(userId, hostId)) {
    usePlayerStore.getState().setPlayerRecoveryStatus('pending');
    startHostRecoveryWatchdog();
  } else {
    usePlayerStore.getState().setPlayerRecoveryStatus('idle');
  }

  ensureSchemaV2ForHost(eventId).catch((error) => {
    console.error('Schema v2 init failed:', error);
  });

  const stopEvent = onSnapshot(eventRef(eventId), (snapshot) => {
    if (!snapshot.exists()) return;
    handleRemoteEventSnapshot(snapshot.id, snapshot.data() || {});
  });

  const stopQueue = onSnapshot(queueRef(eventId), (snapshot) => {
    if (!snapshot.exists()) return;
    handleRemoteQueueSnapshot(snapshot.data() || {});
  });

  const stopPlayer = onSnapshot(playerRef(eventId), (snapshot) => {
    if (!snapshot.exists()) {
      finishHostRecoveryNoCheckpoint(0);
      return;
    }
    handleRemotePlayerSnapshot(snapshot.data() || {});
  }, (error) => {
    console.error('Player snapshot failed:', error);
    finishHostRecoveryNoCheckpoint(0);
  });

  const stopPresence = watchPresence(eventId, (presenceByUid) => {
    useEventStore.getState().setPresenceByUid(presenceByUid);
  });

  if (isEventLive(useEventStore.getState().eventData)) {
    const uid = currentIdentity().userId;
    markPresenceOnline({ eventId, uid }).catch(() => {});
  }

  unsubscribers = [stopEvent, stopQueue, stopPlayer, stopPresence];
  return unsubscribeFromEventSession;
}

export function resetSessionSyncForTests() {
  unsubscribeFromEventSession();
  resetHostSeekForTests();
  publishCalls = 0;
}
